import { Configuration, Group } from '@/api'
import { isAgencyGroup } from '@/sections/settings/AgenciesSettings/agencies-utils'
import {
  filterConfigurationsByGroupId,
  filterConfigurationsByMemberGroupIds,
} from '@/utils/filterConfigurationsByGroupId'

export function getWizardMemberGroupIds(myGroups: Group[] | undefined): Set<string> {
  return new Set(
    (myGroups ?? [])
      .map((group) => group.gid)
      .filter((gid): gid is string => Boolean(gid))
  )
}

/** Groupes organisation (pas d’agence enfant : `parent` vide). */
export function getOrganizationRootGroups(groups: Group[]): Group[] {
  return groups.filter((group) => !group.parent)
}

/** Options du select agence : org racine puis agences / équipes (sans doublon). */
export function getWizardAgencySelectOptions(groups: Group[]): Group[] {
  const roots = getOrganizationRootGroups(groups)
  const agencies = groups.filter(isAgencyGroup)
  if (agencies.length === 0) return roots

  const seen = new Set<string>()
  const options: Group[] = []
  for (const group of [...roots, ...agencies]) {
    if (group.gid && !seen.has(group.gid)) {
      seen.add(group.gid)
      options.push(group)
    }
  }
  return options
}

export function isWizardOrganizationRoot(
  group: Pick<Group, 'parent' | 'type'>
): boolean {
  return !group.parent || group.type === 'ORGANIZATION'
}

export function getDefaultOrgRootGroupId(
  myGroups: Group[] | undefined
): string | undefined {
  return getOrganizationRootGroups(myGroups ?? [])[0]?.gid
}

/** Pré-remplissage : org racine si présente, sinon première agence. */
export function getDefaultWizardGroupId(
  myGroups: Group[] | undefined
): string | undefined {
  if (!myGroups?.length) return undefined
  const orgRoot = getDefaultOrgRootGroupId(myGroups)
  if (orgRoot) return orgRoot
  return myGroups.filter(isAgencyGroup)[0]?.gid
}

/**
 * Préparation intelligente : groupe marqué `selectAsDefault` s'il est dans le
 * select, sinon le défaut historique (organisation racine).
 */
export function getIntelligentWizardGroupId(
  myGroups: Group[] | undefined
): string | undefined {
  const preferred = getWizardAgencySelectOptions(myGroups ?? []).find(
    (group) => group.selectAsDefault && group.gid
  )
  if (preferred?.gid) return preferred.gid
  return getDefaultWizardGroupId(myGroups)
}

/**
 * Configurations proposées dans le wizard : strictement celles du groupe
 * sélectionné (org racine incluse). Sans groupe sélectionné : union des configs
 * des groupes du compte (état transitoire avant défaut Formik).
 */
export function getWizardConfigurations(
  configurations: Configuration[] | undefined,
  selectedGroupId: string,
  myGroups: Group[] | undefined
): Configuration[] {
  if (!configurations?.length) return []

  if (!selectedGroupId) {
    return filterConfigurationsByMemberGroupIds(
      configurations,
      getWizardMemberGroupIds(myGroups)
    )
  }

  return filterConfigurationsByGroupId(configurations, selectedGroupId)
}
